import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Shield, AlertTriangle, Clock, ChevronRight,
  Plus, Activity, Eye, Play, Pause, Package, RotateCcw, X
} from 'lucide-react';
import { Button } from '../components/ui/button';

import { getLogoByIntegrationId } from '../constants/logos';

const securityCases = [
  {
    id: "case_sec_001",
    type: "containment",
    typeLabel: "Containment",
    subject: { name: "Suspicious login - jdoe@acme.com", source: "CrowdStrike", logoId: "crowdstrike" },
    status: "executing",
    priority: "p1",
    threat: "Multiple failed logins from new location",
    actionBundle: {
      actions: ["Block IP", "Force password reset", "Notify user"],
      status: "2/3 complete"
    },
    agents: ["security", "identity"],
    sla: { remaining: "15m", breached: false }
  },
  {
    id: "case_sec_002",
    type: "incident",
    typeLabel: "Security Incident",
    subject: { name: "Malware detected on ACME-LP-0789", source: "CrowdStrike", logoId: "crowdstrike" },
    status: "pending_approval",
    priority: "p1",
    threat: "Trojan detected, lateral movement possible",
    actionBundle: {
      proposed: ["Quarantine device", "Revoke all access", "Wipe device", "Create evidence pack"],
      blastRadius: "1 device, 1 user"
    },
    agents: ["security", "endpoint", "identity"],
    sla: { remaining: "30m", breached: false }
  },
  {
    id: "case_sec_003",
    type: "policy_violation",
    typeLabel: "Policy Violation",
    subject: { name: "3 users sharing credentials", source: "Okta logs", logoId: "okta" },
    status: "pending_approval",
    priority: "p2",
    threat: "Credential sharing detected via concurrent sessions",
    actionBundle: {
      proposed: ["Force password reset", "Enable MFA", "Notify managers"],
      blastRadius: "3 users"
    },
    agents: ["security", "identity"],
    sla: { remaining: "4h", breached: false }
  },
  {
    id: "case_sec_004",
    type: "auto_revocation",
    typeLabel: "Auto-Revocation",
    subject: { name: "Terminated employee access cleanup", user: "Jane Doe" },
    status: "verifying",
    priority: "p1",
    actionBundle: {
      actions: ["Okta deactivated", "Slack removed", "GitHub removed", "Salesforce suspended"],
      status: "Verifying completeness",
      logoIds: ["okta", "slack", "github", "salesforce"]
    },
    agents: ["security", "identity", "saas"],
    sla: { remaining: "1h", breached: false }
  }
];

const playbooks = [
  { name: "Suspicious Login", mode: "execute", autoEnabled: true, lastTriggered: "2h ago" },
  { name: "Malware Detection", mode: "propose", autoEnabled: false, lastTriggered: "3d ago" },
  { name: "Credential Sharing", mode: "observe", autoEnabled: false, lastTriggered: "5d ago" },
  { name: "Data Exfiltration", mode: "propose", autoEnabled: false, lastTriggered: "Never" }
];

const securityMetrics = {
  activeCases: 4,
  containedThreats: 12,
  avgContainmentTime: "28s",
  evidencePacks: 8,
  policyViolations: 5
};

const NewSecurityCaseModal = ({ isOpen, onClose, onCreateCase }) => {
  const [formData, setFormData] = useState({
    title: '',
    threat: '',
    severity: 'p2',
    source: 'Manual'
  });

  if (!isOpen) return null;

  const handleCreate = () => {
    const newCase = {
      id: `case_sec_${Date.now()}`,
      type: 'incident',
      typeLabel: 'Security Incident',
      subject: { name: formData.title, source: formData.source },
      status: 'pending_approval',
      priority: formData.severity,
      threat: formData.threat || 'Manual incident report',
      actionBundle: {
        proposed: ["Investigate"],
        blastRadius: "Unknown"
      },
      agents: ["security"],
      sla: { remaining: "4h", breached: false }
    };
    onCreateCase(newCase);
    onClose();
    setFormData({ title: '', threat: '', severity: 'p2', source: 'Manual' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">New Security Case</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Suspicious activity detected..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Threat Description</label>
            <textarea
              value={formData.threat}
              onChange={(e) => setFormData({ ...formData, threat: e.target.value })}
              placeholder="Describe the threat..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="p1">P1 - Critical</option>
                <option value="p2">P2 - High</option>
                <option value="p3">P3 - Medium</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="Manual">Manual Report</option>
                <option value="CrowdStrike">CrowdStrike</option>
                <option value="Okta">Okta</option>
                <option value="Email">Email Report</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleCreate} disabled={!formData.title}>
            Create Case
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SecurityPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState(securityCases);
  const criticalCases = cases.filter(c => c.priority === 'p1' && c.status === 'pending_approval');
  const [pausedPlaybooks, setPausedPlaybooks] = useState({});
  const [rollingBack, setRollingBack] = useState(null);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);

  const handleCreateCase = (newCase) => {
    setCases([newCase, ...cases]);
  };

  const handlePausePlaybook = (playbookName) => {
    setPausedPlaybooks(prev => ({
      ...prev,
      [playbookName]: !prev[playbookName]
    }));
  };

  const handleRollbackPlaybook = (playbookName) => {
    setRollingBack(playbookName);
    setTimeout(() => {
      setRollingBack(null);
    }, 2000);
  };

  const getStatusBadge = (status) => {
    const config = {
      executing: 'badge-executing',
      pending_approval: 'badge-pending',
      verifying: 'badge-verifying',
      closed: 'badge-active'
    };
    const labels = {
      executing: 'Executing',
      pending_approval: 'Needs Approval',
      verifying: 'Verifying',
      closed: 'Completed'
    };
    return { className: config[status] || 'badge-neutral', label: labels[status] || status };
  };

  const getModeBadge = (mode, isPaused) => {
    if (isPaused) return { className: 'badge-neutral', label: 'Paused', Icon: Pause };
    const config = {
      observe: { className: 'badge-neutral', label: 'Observe', Icon: Eye },
      propose: { className: 'badge-pending', label: 'Propose', Icon: Pause },
      execute: { className: 'badge-active', label: 'Execute', Icon: Play }
    };
    return config[mode] || config.observe;
  };

  return (
    <div className="space-y-6" data-testid="security-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-500" />
            Security
          </h1>
          <p className="text-sm text-slate-500 mt-1">Threat containment, incident response, and evidence packaging</p>
        </div>
        <Button onClick={() => setShowNewCaseModal(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Create Case
        </Button>
      </div>

      {criticalCases.length > 0 && (
        <div className="bg-slate-100 border border-slate-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-slate-700 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-slate-900">
                Critical: {criticalCases[0].typeLabel} requires approval
              </h3>
              <p className="text-sm text-slate-700 mt-1">{criticalCases[0].subject.name}</p>
              <p className="text-xs text-slate-600 mt-1">{criticalCases[0].threat}</p>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-slate-200 border-slate-400 text-slate-800 hover:bg-slate-300"
                  onClick={() => navigate(`/case/${criticalCases[0].id}/review`)}
                >
                  Review Bundle
                </Button>
                <span className="text-xs text-slate-600">SLA: {criticalCases[0].sla.remaining}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Active Cases</span>
            <Activity className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{securityMetrics.activeCases}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Contained (7d)</span>
            <Shield className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{securityMetrics.containedThreats}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Avg Containment</span>
            <Clock className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{securityMetrics.avgContainmentTime}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Evidence Packs</span>
            <Package className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{securityMetrics.evidencePacks}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Security Cases</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {cases.map((caseItem) => {
                const statusBadge = getStatusBadge(caseItem.status);
                return (
                  <div
                    key={caseItem.id}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/case/${caseItem.id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${caseItem.subject.logoId ? 'bg-white border border-slate-200' : (caseItem.priority === 'p1' ? 'bg-slate-900' : 'bg-slate-400')}`}>
                        {caseItem.subject.logoId ? (
                          <img
                            src={getLogoByIntegrationId(caseItem.subject.logoId).src}
                            alt={getLogoByIntegrationId(caseItem.subject.logoId).alt}
                            className="w-5 h-5 object-contain"
                          />
                        ) : null}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`badge-status ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                          <span className="text-sm font-medium text-slate-900">{caseItem.typeLabel}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{caseItem.subject.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{caseItem.threat}</p>

                        <div className="mt-2 p-2 bg-slate-50 rounded">
                          <div className="text-xs font-medium text-slate-700 mb-1">Action Bundle:</div>
                          {caseItem.actionBundle.actions && (
                            <div className="text-xs text-slate-600">
                              {caseItem.actionBundle.logoIds ? (
                                <div className="flex flex-wrap gap-2 mb-1">
                                  {caseItem.actionBundle.logoIds.map(lid => {
                                    const logo = getLogoByIntegrationId(lid);
                                    return logo ? <img key={lid} src={logo.src} alt={logo.alt} className="w-4 h-4 object-contain" /> : null;
                                  })}
                                </div>
                              ) : null}
                              {caseItem.actionBundle.actions.join(" → ")}
                              <span className="ml-2 text-primary">{caseItem.actionBundle.status}</span>
                            </div>
                          )}
                          {caseItem.actionBundle.proposed && (
                            <div className="text-xs text-slate-600">
                              Proposed: {caseItem.actionBundle.proposed.join(", ")}
                              <span className="block mt-1 text-slate-600">
                                Blast radius: {caseItem.actionBundle.blastRadius}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-1 mt-2">
                          {caseItem.agents.map((agent) => (
                            <span key={agent} className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                              {agent}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400">{caseItem.sla.remaining}</div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Playbook Autonomy</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {playbooks.map((playbook, idx) => {
                const isPaused = pausedPlaybooks[playbook.name];
                const modeBadge = getModeBadge(playbook.mode, isPaused);
                const ModeIcon = modeBadge.Icon;
                const isRollingBackThis = rollingBack === playbook.name;
                return (
                  <div key={idx} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900">{playbook.name}</span>
                      <span className={`badge-status ${modeBadge.className} inline-flex items-center gap-1`}>
                        <ModeIcon className="w-3 h-3" />
                        {modeBadge.label}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">Last: {playbook.lastTriggered}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handlePausePlaybook(playbook.name)}
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors ${isPaused
                          ? 'bg-primary/10 text-primary hover:bg-primary/20'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        data-testid={`pause-playbook-${idx}`}
                      >
                        {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                        {isPaused ? 'Resume' : 'Pause'}
                      </button>
                      <button
                        onClick={() => handleRollbackPlaybook(playbook.name)}
                        disabled={isRollingBackThis}
                        className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center gap-1 disabled:opacity-50"
                        data-testid={`rollback-playbook-${idx}`}
                      >
                        <RotateCcw className={`w-3 h-3 ${isRollingBackThis ? 'animate-spin' : ''}`} />
                        {isRollingBackThis ? 'Rolling back...' : 'Rollback'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Security Agent</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Trust Score</span>
                <span className="font-medium text-primary">99.5%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Rollback Rate</span>
                <span className="font-medium text-slate-900">0%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Containment Speed</span>
                <span className="font-medium text-slate-900">&lt; 30s</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NewSecurityCaseModal
        isOpen={showNewCaseModal}
        onClose={() => setShowNewCaseModal(false)}
        onCreateCase={handleCreateCase}
      />
    </div>
  );
};

export default SecurityPage;
